import { useState } from "react";
import { Stack, Group, Text, Button, Select, Box, Collapse, Modal, TextInput, Checkbox } from "@mantine/core";
import {
  useViewerStore,
  useJointGroups,
  useSuggestedGroups,
  useShowSuggestedGroupsModal,
  useJoints,
  useAvailableLinks,
  useSelectedGroupFilter,
} from "../../store";
import { useWebSocket } from "../../WebSocket";
import { DualPaneJointSelector } from "./DualPaneJointSelector";
import { ChainSelector } from "./ChainSelector";

// Preset colors for joint groups
const GROUP_COLORS = [
  "#228be6", // blue
  "#40c057", // green
  "#fa5252", // red
  "#fab005", // yellow
  "#7950f2", // violet
  "#fd7e14", // orange
  "#20c997", // teal
  "#e64980", // pink
];


// Joint Groups Section with MoveIt-style UX
export function JointGroupsSection() {
  const jointGroups = useJointGroups();
  const suggestedGroups = useSuggestedGroups();
  const showSuggestedGroupsModal = useShowSuggestedGroupsModal();
  const joints = useJoints();
  const availableLinks = useAvailableLinks();
  const selectedGroupFilter = useSelectedGroupFilter();
  const setShowSuggestedGroupsModal = useViewerStore((state) => state.setShowSuggestedGroupsModal);
  const setSelectedGroupFilter = useViewerStore((state) => state.setSelectedGroupFilter);
  const { sendCreateJointGroup, sendUpdateJointGroup, sendDeleteJointGroup, sendDetectKinematicChains, sendHighlightJoint } = useWebSocket();

  const [showEditorModal, setShowEditorModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<string | null>(null);
  const [formName, setFormName] = useState("");
  const [formColor, setFormColor] = useState<string>(GROUP_COLORS[0]);
  const [formJoints, setFormJoints] = useState<string[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [selectedSuggestions, setSelectedSuggestions] = useState<string[]>([]);
  const [selectionMode, setSelectionMode] = useState<"joints" | "chain">("joints");

  const resetForm = () => {
    setFormName("");
    setFormColor(GROUP_COLORS[0]);
    setFormJoints([]);
    setShowEditorModal(false);
    setEditingGroup(null);
    setSelectionMode("joints");
    sendHighlightJoint(""); // Clear highlight
  };

  const handleSave = () => {
    if (!formName) return;
    if (editingGroup) {
      const newName = formName !== editingGroup ? formName : null;
      sendUpdateJointGroup(editingGroup, newName, formJoints, formColor);
    } else {
      sendCreateJointGroup(formName, formJoints, formColor);
    }
    resetForm();
  };

  const handleOpenAdd = () => {
    setEditingGroup(null);
    setFormName("");
    setFormColor(GROUP_COLORS[Math.floor(Math.random() * GROUP_COLORS.length)]);
    setFormJoints([]);
    setSelectionMode("joints");
    setShowEditorModal(true);
  };

  const handleEdit = (groupName: string) => {
    const group = jointGroups.find((g) => g.name === groupName);
    if (!group) return;
    setEditingGroup(groupName);
    setFormName(group.name);
    setFormColor(group.color || GROUP_COLORS[0]);
    setFormJoints([...group.joint_names]);
    setSelectionMode("joints");
    setShowEditorModal(true);
  };

  const handleDelete = (groupName: string) => {
    sendDeleteJointGroup(groupName);
  };

  const toggleGroupExpanded = (groupName: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  const handleAutoDetect = () => {
    sendDetectKinematicChains();
  };

  const handleCreateSuggested = () => {
    for (const suggestionName of selectedSuggestions) {
      const suggestion = suggestedGroups.find((s) => s.name === suggestionName);
      if (suggestion) {
        const colorIndex = jointGroups.length % GROUP_COLORS.length;
        sendCreateJointGroup(suggestion.name, suggestion.joint_names, GROUP_COLORS[colorIndex]);
      }
    }
    setSelectedSuggestions([]);
    setShowSuggestedGroupsModal(false);
  };

  const handleHighlightJoint = (jointName: string) => {
    sendHighlightJoint(jointName);
  };

  const handleChainSelected = (chainJoints: string[]) => {
    setFormJoints(chainJoints);
    setSelectionMode("joints"); // Switch back to joints view to show selection
  };

  const getJointDisplayName = (uniqueName: string): string => {
    // Show full unique name including prefix (prefix/joint_name)
    return uniqueName;
  };

  const isEditing = editingGroup !== null;

  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <Text size="xs" fw={500}>Joint Groups</Text>
        <Group gap={4}>
          <Button size="xs" variant="light" onClick={handleAutoDetect} disabled={joints.length === 0}>
            Auto-Detect
          </Button>
          <Button size="xs" variant="light" onClick={handleOpenAdd} disabled={joints.length === 0}>
            + Add
          </Button>
        </Group>
      </Group>

      {/* Group Filter */}
      {jointGroups.length > 0 && (
        <Select
          size="xs"
          placeholder="Filter by group"
          data={[{ value: "", label: "Show all joints" }, ...jointGroups.map((g) => ({ value: g.name, label: g.name }))]}
          value={selectedGroupFilter || ""}
          onChange={(val) => setSelectedGroupFilter(val || null)}
          clearable
        />
      )}

      {joints.length === 0 && <Text size="xs" c="dimmed">Add modules to create joint groups</Text>}

      {/* Existing groups list */}
      {jointGroups.map((group) => (
        <Box
          key={group.name}
          p="xs"
          style={{ border: "1px solid var(--mantine-color-gray-3)", borderRadius: 4 }}
        >
          <Group justify="space-between" align="flex-start">
            <Group gap="xs" style={{ cursor: "pointer" }} onClick={() => toggleGroupExpanded(group.name)}>
              <Text size="xs">{expandedGroups[group.name] ? "▼" : "▶"}</Text>
              <Box style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: group.color || GROUP_COLORS[0] }} />
              <Text size="xs" fw={500}>{group.name}</Text>
              <Text size="xs" c="dimmed">({group.joint_names.length})</Text>
            </Group>
            <Group gap={4}>
              <Button size="xs" variant="subtle" px={6} onClick={(e) => { e.stopPropagation(); handleEdit(group.name); }}>Edit</Button>
              <Button size="xs" variant="subtle" color="red" px={6} onClick={(e) => { e.stopPropagation(); handleDelete(group.name); }}>×</Button>
            </Group>
          </Group>
          <Collapse in={expandedGroups[group.name]}>
            <Box mt="xs" pl="md">
              {group.joint_names.map((jointName) => (
                <Text key={jointName} size="xs" c="dimmed">└─ {getJointDisplayName(jointName)}</Text>
              ))}
            </Box>
          </Collapse>
        </Box>
      ))}

      {/* Joint Group Editor Modal (MoveIt-style) */}
      <Modal
        opened={showEditorModal}
        onClose={resetForm}
        title={isEditing ? `Edit Group: ${editingGroup}` : "Create Joint Group"}
        size="lg"
      >
        <Stack gap="md">
          {/* Name and Color */}
          <Group grow align="flex-end">
            <TextInput
              label="Group Name"
              placeholder="e.g., right_arm"
              size="sm"
              value={formName}
              onChange={(e) => setFormName(e.currentTarget.value)}
            />
            <Box>
              <Text size="xs" c="dimmed" mb={4}>Color</Text>
              <Group gap={4}>
                {GROUP_COLORS.map((color) => (
                  <Box
                    key={color}
                    style={{
                      width: 24, height: 24, borderRadius: "50%", backgroundColor: color, cursor: "pointer",
                      border: formColor === color ? "3px solid var(--mantine-color-dark-9)" : "2px solid transparent",
                    }}
                    onClick={() => setFormColor(color)}
                  />
                ))}
              </Group>
            </Box>
          </Group>

          {/* Selection Mode Tabs */}
          <Box>
            <Group gap="xs" mb="sm">
              <Button
                size="xs"
                variant={selectionMode === "joints" ? "filled" : "light"}
                onClick={() => setSelectionMode("joints")}
              >
                Select Joints
              </Button>
              <Button
                size="xs"
                variant={selectionMode === "chain" ? "filled" : "light"}
                onClick={() => setSelectionMode("chain")}
              >
                Select Chain
              </Button>
              <Text size="xs" c="dimmed" ml="auto">
                Tip: Hold Shift to select range
              </Text>
            </Group>

            {selectionMode === "joints" ? (
              <DualPaneJointSelector
                availableJoints={joints}
                selectedJoints={formJoints}
                onSelectionChange={setFormJoints}
                onHighlightJoint={handleHighlightJoint}
              />
            ) : (
              <ChainSelector
                availableLinks={availableLinks}
                onChainSelected={handleChainSelected}
              />
            )}
          </Box>

          {/* Actions */}
          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={resetForm}>Cancel</Button>
            <Button color="blue" onClick={handleSave} disabled={!formName || formJoints.length === 0}>
              {isEditing ? "Update" : "Create"} ({formJoints.length} joints)
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Suggested Groups Modal */}
      <Modal
        opened={showSuggestedGroupsModal}
        onClose={() => setShowSuggestedGroupsModal(false)}
        title="Suggested Groups from Kinematic Chains"
        size="md"
      >
        <Stack gap="md">
          {suggestedGroups.length === 0 ? (
            <Text size="sm" c="dimmed">No kinematic chains detected.</Text>
          ) : (
            <>
              <Text size="xs" c="dimmed">Select the groups you want to create:</Text>
              {suggestedGroups.map((suggestion) => (
                <Checkbox
                  key={suggestion.name}
                  label={
                    <Box>
                      <Text size="sm" fw={500}>{suggestion.name} ({suggestion.joint_names.length} joints)</Text>
                      <Text size="xs" c="dimmed">{suggestion.description}</Text>
                    </Box>
                  }
                  checked={selectedSuggestions.includes(suggestion.name)}
                  onChange={(e) => {
                    if (e.currentTarget.checked) {
                      setSelectedSuggestions((prev) => [...prev, suggestion.name]);
                    } else {
                      setSelectedSuggestions((prev) => prev.filter((n) => n !== suggestion.name));
                    }
                  }}
                />
              ))}
              <Group justify="flex-end" gap="xs">
                <Button variant="default" onClick={() => setShowSuggestedGroupsModal(false)}>Cancel</Button>
                <Button color="blue" onClick={handleCreateSuggested} disabled={selectedSuggestions.length === 0}>
                  Create Selected ({selectedSuggestions.length})
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </Modal>
    </Stack>
  );
}

